/**
 * Administration: who may sign in, the bot sync, what is connected, and where
 * the record came from.
 */
import { useEffect, useState } from 'react';
import type { Staff, SyncRun } from '@shared/model';
import { prettyPhone } from '@shared/model';
import { apiGet, apiPost, apiUrl } from '../api/client';
import { fmtDate } from '../components/FarmerCard';
import { useRoster } from '../state/RosterContext';
import { useSession } from '../state/SessionContext';

interface Health { farms: number; holders: number; whatsapp: boolean; lastSync: SyncRun | null }
interface SyncStatus { last: SyncRun | null; source: string }

export function Admin(): JSX.Element {
  const { account } = useSession();
  const { staff, reloadStaff, summary, villages, sync, whatsapp } = useRoster();
  const [health, setHealth] = useState<Health | null>(null);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [form, setForm] = useState({ email: '', name: '', role: 'officer' as Staff['role'], villages: [] as string[] });

  const refresh = (): void => {
    void apiGet<Health>('/api/health').then((r) => r.status === 'ready' && r.data && setHealth(r.data));
    void apiGet<SyncStatus>('/api/sync/status').then((r) => r.status === 'ready' && r.data && setSyncStatus(r.data));
  };
  useEffect(refresh, []);

  const add = async (): Promise<void> => {
    const r = await apiPost<Staff[]>('/api/staff', form);
    setMsg(r.status === 'ready' ? `Saved ${form.email}.` : r.status === 'error' || r.status === 'not_configured' ? r.message : 'Not saved.');
    if (r.status === 'ready') { setForm({ email: '', name: '', role: 'officer', villages: [] }); reloadStaff(); }
  };
  const remove = async (email: string): Promise<void> => {
    if (!window.confirm(`Remove ${email} from the staff list?`)) return;
    await fetch(apiUrl(`/api/staff/${encodeURIComponent(email)}`), { method: 'DELETE', credentials: 'same-origin' });
    reloadStaff();
  };
  const runSync = async (): Promise<void> => { setSyncing(true); setMsg(await sync()); setSyncing(false); refresh(); };

  if (!account?.admin) return <div className="page"><section className="card card--pad"><h3 className="card__title">Administrators only</h3><p className="card__empty">{account?.email} is not an administrator. Ask the programme lead to add you to ADMIN_EMAILS on the server or mark you admin on the staff list.</p></section></div>;

  return (
    <div className="page">
      <div className="page__head"><div><h1>Admin</h1><p>Staff, the WhatsApp bot sync, connections and provenance.</p></div></div>
      {msg ? <div className="notice notice--ok">{msg}</div> : null}

      <div className="grid-2">
        <section className="card card--pad">
          <div className="card__head"><h3>Staff who may sign in</h3></div>
          <div className="admin-form">
            <label>E-mail<input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@adityabirla.com" /></label>
            <label>Name<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
            <label>Role<select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as Staff['role'] })}><option value="officer">Field officer</option><option value="admin">Administrator</option></select></label>
            <button type="button" className="btn-primary" disabled={!form.email.includes('@')} onClick={() => void add()}>Add</button>
          </div>
          <label className="muted" style={{ display: 'block', marginTop: 10, fontSize: 12 }}>Villages (optional)
            <select multiple value={form.villages} onChange={(e) => setForm({ ...form, villages: [...e.target.selectedOptions].map((o) => o.value) })} style={{ display: 'block', width: '100%', marginTop: 4, minHeight: 90, font: 'inherit', background: 'var(--surface-2)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 6 }}>
              {villages.map((v) => <option key={v.village} value={v.village}>{v.village}</option>)}
            </select>
          </label>
          <table className="vtable" style={{ marginTop: 12 }}>
            <thead><tr><th>Name</th><th>E-mail</th><th>Role</th><th>Villages</th><th></th></tr></thead>
            <tbody>
              {staff.map((s) => (
                <tr key={s.email}><td>{s.name}</td><td className="mono">{s.email}</td><td>{s.role}</td><td className="muted">{s.villages.join(', ') || 'all'}</td><td><button type="button" className="btn-ghost" onClick={() => void remove(s.email)}>Remove</button></td></tr>
              ))}
              {!staff.length ? <tr><td colSpan={5} className="muted">No staff yet. Administrators listed in ADMIN_EMAILS on the server can always sign in.</td></tr> : null}
            </tbody>
          </table>
          <p className="card__note" style={{ marginTop: 8 }}>Sign-in is e-mail plus the shared access code (PORTAL_ACCESS_CODE on the server). AUTH_DISABLED=1 on this machine means every request is the development account.</p>
        </section>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <section className="card card--pad">
            <div className="card__head"><h3>WhatsApp bot sync</h3><span className="spacer" /><button type="button" className="btn-primary" disabled={syncing} onClick={() => void runSync()}>{syncing ? 'Syncing…' : 'Sync now'}</button></div>
            <dl className="fc" style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '5px 12px', margin: 0, fontSize: 13 }}>
              <dt className="muted">Source</dt><dd style={{ margin: 0 }} className="mono">{syncStatus?.source ?? '…'}</dd>
              <dt className="muted">Last run</dt><dd style={{ margin: 0 }}>{syncStatus?.last ? `${fmtDate(syncStatus.last.at)} by ${syncStatus.last.by} · ${syncStatus.last.farmers_read} read, ${syncStatus.last.matched} placed, ${syncStatus.last.outside} outside` : 'never'}</dd>
            </dl>
            <p className="card__note" style={{ marginTop: 8 }}>On the SHC box set BOT_DB and FIELDDESK_DB to the bot's live SQLite files (read-only). Elsewhere, run <code>python tools/snapshot_bot.py</code> against a backup and the sync reads data/bot-snapshot.json.</p>
          </section>

          <section className="card card--pad">
            <div className="card__head"><h3>Connections</h3></div>
            <dl style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '5px 12px', margin: 0, fontSize: 13 }}>
              <dt className="muted">Bot number</dt><dd style={{ margin: 0 }}>{whatsapp ? prettyPhone(whatsapp.number) : '…'}</dd>
              <dt className="muted">Direct sending</dt><dd style={{ margin: 0 }}>{health ? (health.whatsapp ? <span className="ok">on — official deep links minted, text can be sent from here</span> : <span className="muted">off — plain wa.me links; set WHATSAPP_TOKEN and WHATSAPP_PHONE_ID</span>) : '…'}</dd>
              <dt className="muted">Record loaded</dt><dd style={{ margin: 0 }}>{health ? `${health.farms.toLocaleString('en-IN')} farms · ${health.holders.toLocaleString('en-IN')} holders` : '…'}</dd>
            </dl>
          </section>

          <section className="card card--pad">
            <div className="card__head"><h3>Where the record comes from</h3></div>
            {summary ? (
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                <li>{summary.generated_from}</li>
                <li>{summary.holder_rule}</li>
                <li>{summary.pin_rule} ({Object.entries(summary.pin_method).map(([k, v]) => `${v} ${k}`).join(', ')})</li>
                <li>{summary.farms_unnamed} khasras carry no name on the record and are on the map but not in the roster.</li>
                {summary.villages_missing_from_extract.length ? <li>Not in the extract: {summary.villages_missing_from_extract.join(', ')}.</li> : null}
              </ul>
            ) : null}
          </section>
        </div>
      </div>
    </div>
  );
}
